import classNames from 'classnames';
import { secondsToMilliseconds } from 'date-fns';
import {
    cloneElement,
    createContext,
    DetailedHTMLProps,
    HTMLAttributes,
    isValidElement,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useState,
    FunctionComponentElement,
    ComponentProps,
} from 'react';
import { Toast } from 'shared/Toast';

const TOAST_TIMEOUT = secondsToMilliseconds(3);
const ERROR_TOAST_TIMEOUT = secondsToMilliseconds(20);

type ToastElement = FunctionComponentElement<ComponentProps<typeof Toast>>;

type ToastsState = {
    id: string;
    toast: ToastElement;
}[];

interface ToastOptions {
    id?: string;
    timeout?: number | null;
}

export interface ToastContextValue {
    hideToast: (id?: string) => void;
    showToast: (toast: ToastElement, options?: ToastOptions) => () => void;
    toasts: ToastsState;
}

export const ToastContext = createContext<ToastContextValue>(null!);

export interface ToastProviderProps
    extends
        PropsWithChildren,
        Pick<
            DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
            'className'
        > {}

export const ToastProvider = ({ children, className }: ToastProviderProps) => {
    const [toasts, setToasts] = useState<ToastsState>([]);

    const hideToast = useCallback(
        (id?: string) =>
            setToasts((currentToasts) =>
                id
                    ? currentToasts.filter(
                          (currentToast) => currentToast?.id !== id
                      )
                    : []
            ),
        []
    );

    const showToast = useCallback(
        (toast: ToastElement, options?: ToastOptions) => {
            if (!isValidElement(toast)) {
                return () => {};
            }

            const id = options?.id ?? crypto.randomUUID();

            setToasts((currentToasts) => [
                ...currentToasts,
                { id, toast: cloneElement(toast, { key: id }) },
            ]);

            if (options?.timeout !== null) {
                const isFailureToast = toast.props.color === 'failure';
                const defaultTimeout = isFailureToast
                    ? ERROR_TOAST_TIMEOUT
                    : TOAST_TIMEOUT;

                setTimeout(
                    () => hideToast(id),
                    options?.timeout ?? defaultTimeout
                );
            }

            return () => hideToast(id);
        },
        [hideToast]
    );

    const value = useMemo(
        () => ({ hideToast, showToast, toasts }),
        [hideToast, showToast, toasts]
    );

    return (
        <ToastContext value={value}>
            {children}

            <div
                className={classNames(
                    'bottom-4 flex flex-col fixed gap-2 max-w-[28rem] overflow-hidden right-4 w-full z-(--z-toast)',
                    className
                )}
            >
                {toasts.map(({ toast }) => toast)}
            </div>
        </ToastContext>
    );
};

export const useToastContext = () => {
    const toastContext = useContext(ToastContext);

    if (!toastContext) {
        throw new Error('useToastContext must be used within a ToastProvider');
    }

    return toastContext;
};
